/**
 * Zeilenfärbung und Fade — Portierung von RowColor(), DotColor(),
 * TintForState(), BlinkTint(), LerpColor() und ComputeBlinkParam()
 * aus CanMonitorApp.cpp.
 *
 * Die Farbwerte stehen bewusst hier als Literale und nicht in tokens.css:
 * im Original sind es ebenfalls Literale in der Funktion und keine
 * Palettenwerte (kPalettes kennt sie nicht). Nur die Zielfarbe des Fades
 * (Feldhintergrund) kommt aus der Palette.
 */

import { isDarkMode, onThemeChanged, themeColor } from '../theme';

export interface Rgb {
  r: number;
  g: number;
  b: number;
}

/** Zeilenhintergrund: dunkel entsättigt bzw. hell pastellig, damit Text lesbar bleibt. */
const ROW_TINT_DARK = {
  green: { r: 0x1a, g: 0x3a, b: 0x1e },
  orange: { r: 0x4a, g: 0x35, b: 0x10 },
  red: { r: 0x4a, g: 0x14, b: 0x14 },
} as const;

/**
 * Anteil Feldhintergrund im Zeilenton der hellen Themes. Fest gemischte
 * Pastelltoene passten nur zu einem der drei (das kuehle Minzgruen stach im
 * warmen Bright-Beige heraus); so traegt jeder Ton den Unterton seines Themes.
 */
const LIGHT_TINT_BACKGROUND_MIX = 0.8;

/** Punktmodus: kein Text darueber, deshalb kraeftig -- in den hellen Themes
 *  aber gedaempft, dort glaenzten die Neonfarben gegen die weichen Paletten. */
const DOT_TINT = {
  green: { r: 0x1d, g: 0xd1, b: 0x4a },
  orange: { r: 0xff, g: 0x8c, b: 0x00 },
  red: { r: 0xe8, g: 0x1b, b: 0x1b },
} as const;

const DOT_TINT_LIGHT = {
  green: { r: 0x12, g: 0x8a, b: 0x3e },
  orange: { r: 0xc2, g: 0x62, b: 0x00 },
  red: { r: 0xb4, g: 0x1b, b: 0x1b },
} as const;

/**
 * Drosselung für Neustarts des Orange-Blitzens (ComputeBlinkParam): ohne sie
 * wirkt dichter Unbekannt-Verkehr wie durchgehendes Flimmern statt Blinken.
 */
export const ORANGE_FLASH_THROTTLE_MS = 600;

export function parseCssColor(value: string): Rgb {
  const text = value.trim();
  if (text.startsWith('#')) {
    const hex = text.length === 4
      ? text[1]! + text[1]! + text[2]! + text[2]! + text[3]! + text[3]!
      : text.slice(1, 7);
    const n = Number.parseInt(hex, 16);
    return { r: (n >> 16) & 0xff, g: (n >> 8) & 0xff, b: n & 0xff };
  }
  const match = text.match(/-?\d+(\.\d+)?/g);
  if (match !== null && match.length >= 3) {
    return { r: Number(match[0]), g: Number(match[1]), b: Number(match[2]) };
  }
  return { r: 0, g: 0, b: 0 };
}

export function rgbToCss(color: Rgb): string {
  return `rgb(${color.r | 0},${color.g | 0},${color.b | 0})`;
}

/** LerpColor(): kanalweise linear, t auf [0,1] geklemmt. */
export function lerpColor(a: Rgb, b: Rgb, t: number): Rgb {
  const k = t < 0 ? 0 : t > 1 ? 1 : t;
  return {
    r: a.r + (b.r - a.r) * k,
    g: a.g + (b.g - a.g) * k,
    b: a.b + (b.b - a.b) * k,
  };
}

/** Zustand einer Zeile für die Farbwahl (entspricht dem gepackten blinkParam). */
export interface RowState {
  /** Spalte "Name" ist nicht leer — im Original `!cols[11].empty()`. */
  known: boolean;
  isError: boolean;
  /** performance.now()-Zeitpunkt der letzten Aktualisierung. */
  lastUpdateMs: number;
}

export interface ColorSettings {
  /** Help > Fade Speed; 0 = statisch, sonst Fadedauer in ms. */
  rxFadeMs: number;
  /**
   * DecodingActive(): ohne jede Dekodierquelle sind "bekannt"/"unbekannt"
   * bedeutungslos, dann bleiben die Zeilen ungefärbt.
   */
  decodingActive: boolean;
}

/**
 * Cache der Palettenfarben. Canvas kann keine CSS-Variablen auflösen, und
 * getComputedStyle() je Zelle wäre bei 60 Hz zu teuer.
 */
export class TablePalette {
  field: Rgb = { r: 0, g: 0, b: 0 };
  fieldCss = '#000';
  panelCss = '#000';
  textMutedCss = '#fff';
  textPrimaryCss = '#fff';
  textDimCss = '#888';
  accentCss = '#0f0';
  borderCss = '#333';
  tableAccentCss = '#0f0';
  tableSelectionCss = '#123';
  tableSelection: Rgb = { r: 0, g: 0, b: 0 };
  tableHeaderCss = '#222';
  private dark = true;
  private tints: { green: Rgb; orange: Rgb; red: Rgb } = ROW_TINT_DARK;
  private dots: { green: Rgb; orange: Rgb; red: Rgb } = DOT_TINT;
  private readonly stopListening: () => void;

  constructor() {
    this.refresh();
    this.stopListening = onThemeChanged(() => this.refresh());
  }

  refresh(): void {
    this.fieldCss = themeColor('field');
    this.field = parseCssColor(this.fieldCss);
    this.panelCss = themeColor('panel');
    this.textMutedCss = themeColor('text-muted');
    this.textPrimaryCss = themeColor('text-primary');
    this.textDimCss = themeColor('text-dim');
    this.accentCss = themeColor('accent');
    this.borderCss = themeColor('frame-border');
    this.tableAccentCss = themeColor('table-accent');
    this.tableSelectionCss = themeColor('table-selection');
    this.tableSelection = parseCssColor(this.tableSelectionCss);
    this.tableHeaderCss = themeColor('table-header');
    this.dark = isDarkMode();
    this.dots = this.dark ? DOT_TINT : DOT_TINT_LIGHT;
    this.tints = this.dark
      ? ROW_TINT_DARK
      : {
          green: lerpColor(DOT_TINT.green, this.field, LIGHT_TINT_BACKGROUND_MIX),
          orange: lerpColor(DOT_TINT.orange, this.field, LIGHT_TINT_BACKGROUND_MIX),
          red: lerpColor(DOT_TINT.red, this.field, LIGHT_TINT_BACKGROUND_MIX),
        };
  }

  get rowTints(): { green: Rgb; orange: Rgb; red: Rgb } {
    return this.tints;
  }

  get dotTints(): { green: Rgb; orange: Rgb; red: Rgb } {
    return this.dots;
  }

  dispose(): void {
    this.stopListening();
  }
}

/** BlinkTint(): Aufblitzen bei Aktualisierung, dann Fade zum Zeilenhintergrund. */
function blinkTint(color: Rgb, state: RowState, backdrop: Rgb, fadeMs: number, now: number): Rgb {
  const elapsed = now - state.lastUpdateMs;
  if (fadeMs === 0 || elapsed >= fadeMs) return backdrop;
  return lerpColor(color, backdrop, elapsed / fadeMs);
}

/** TintForState(): Fehler schlägt unbekannt schlägt bekannt. */
function tintForState(
  state: RowState,
  backdrop: Rgb,
  settings: ColorSettings,
  tints: { green: Rgb; orange: Rgb; red: Rgb },
  now: number,
): Rgb {
  const color = state.isError ? tints.red : state.known ? tints.green : tints.orange;
  // rxFadeMs === 0 ist "Static": durchgehend gefärbt, kein Ausblenden.
  if (settings.rxFadeMs === 0) return color;
  return blinkTint(color, state, backdrop, settings.rxFadeMs, now);
}

/** RowColor(): Hintergrund der ganzen Zeile im Modus WholeRow. */
export function rowColor(
  state: RowState,
  palette: TablePalette,
  settings: ColorSettings,
  now: number,
): Rgb {
  if (!settings.decodingActive) return palette.field;
  return tintForState(state, palette.field, settings, palette.rowTints, now);
}

/**
 * DotColor(): Farbe des Punkts links von Spalte "n" im Modus Dots.
 * `backdrop` ist der tatsächliche Zeilenhintergrund — der ausgeblendete Punkt
 * muss darin verschwinden, sonst steht er als dunkler Kreis in der markierten Zeile.
 */
export function dotColor(
  state: RowState,
  palette: TablePalette,
  settings: ColorSettings,
  now: number,
  backdrop: Rgb = palette.field,
): Rgb {
  if (!settings.decodingActive) return backdrop;
  return tintForState(state, backdrop, settings, palette.dotTints, now);
}

/**
 * ComputeBlinkParam(): liefert den neuen `lastUpdateMs`-Wert einer Zeile.
 * Für unbekannte Nicht-Fehlerzeilen wird der Neustart des Blitzens
 * gedrosselt (siehe ORANGE_FLASH_THROTTLE_MS).
 *
 * Abweichung: das Original packt Zeitstempel und Fehlerbit in ein DWORD,
 * weil der ListView nur einen LPARAM je Zeile trägt. Hier sind es zwei
 * Felder — semantisch identisch, ohne den 31-Bit-Überlauf.
 */
export function nextUpdateTick(
  previousMs: number,
  known: boolean,
  isError: boolean,
  now: number,
): number {
  if (!known && !isError && now - previousMs < ORANGE_FLASH_THROTTLE_MS) return previousMs;
  return now;
}

/** true, solange irgendeine Zeile noch mitten im Fade steckt (Repaint nötig). */
export function isFading(state: RowState, settings: ColorSettings, now: number): boolean {
  if (settings.rxFadeMs === 0 || !settings.decodingActive) return false;
  return now - state.lastUpdateMs < settings.rxFadeMs;
}
